import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CompanyResponse, CompanyCategoryResponse, MarketDealResponse } from '../../@shared/models/response.module';
import { CompanyService } from '../../@shared/services/company.service';
import { GeocodingService } from '../../@shared/services/geocoding.service';
import { MapMarker, MapConfig } from '../../@shared/components/location-map/location-map';
import { COR_COY_4 } from '../../@shared/models/input.module';
import { TranslateService } from '@ngx-translate/core';
import { EndpointService } from '../../@shared/endpoints.service';

@Component({
  selector: 'app-stores-services',
  standalone: false,
  templateUrl: './stores-services.html',
  styleUrl: './stores-services.css'
})
export class StoresServices implements OnInit {
  companies: CompanyResponse[] = [];
  filteredCompanies: CompanyResponse[] = [];

  // Filters
  searchQuery: string = '';
  selectedCategory: number | null = null;
  sortByDistance: boolean = false;

  // Categories from backend
  categories: CompanyCategoryResponse[] = [];
  categoryOptions: { id: number | null; name: string }[] = [];
  loadingCategories: boolean = false;

  // User location
  userLocation: { lat: number; lng: number } | null = null;
  locationLoading: boolean = false;
  locationError: string | null = null;

  // Company coordinates cache (from geocoding)
  companyCoordinates: { [companyId: string]: { lat: number; lng: number } } = {};

  // Loading state
  loading: boolean = false;

  // Hot Deals
  activeDeals: MarketDealResponse[] = [];
  loadingDeals: boolean = false;
  // Map company_id to active deals for badge display
  companyDealsMap: { [companyId: string]: MarketDealResponse[] } = {};

  // Map properties
  mapMarkers: MapMarker[] = [];
  mapConfig: MapConfig = {
    height: '400px',
    mobileHeight: '300px',
    enableClustering: true,
    defaultCenter: { lat: 59.9139, lng: 10.7522 }, // Oslo, Norway
    defaultZoom: 5
  };
  geocodingInProgress: boolean = false;

  // Query param for pre-selecting category
  private pendingCategorySlug: string | null = null;

  constructor(
    private companyService: CompanyService,
    private geocodingService: GeocodingService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private endpointService: EndpointService
  ) {}

  private readFiltersFromUrl() {
    const params = this.route.snapshot.queryParams;
    if (params['search']) this.searchQuery = params['search'];
    if (params['category']) {
      this.pendingCategorySlug = params['category'];
    }
    if (params['sortByDistance'] === 'true') this.sortByDistance = true;
  }

  private updateUrlWithFilters() {
    const queryParams: any = {};
    if (this.searchQuery) queryParams.search = this.searchQuery;
    if (this.selectedCategory) {
      // Find the category and use its slug
      const category = this.categories.find(c => c.category_id === this.selectedCategory);
      if (category) {
        queryParams.category = (category.name_en || category.name || '').toLowerCase().replace(/\s+/g, '_');
      }
    }
    if (this.sortByDistance) queryParams.sortByDistance = 'true';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: '',
      replaceUrl: true
    });
  }

  ngOnInit() {
    this.readFiltersFromUrl();

    this.loadCategories();
    this.loadCompanies();
    this.loadActiveDeals();
    this.getUserLocation();

    // Re-build category options when language changes
    this.translateService.onLangChange.subscribe(() => {
      this.buildCategoryOptions();
    });
  }

  private applyCategoryFromSlug(slug: string) {
    // Find category by slug (matches name, name_en, or name_no, case-insensitive)
    const normalizedSlug = slug.toLowerCase().replace(/_/g, ' ');
    const category = this.categories.find(cat =>
      cat.name?.toLowerCase() === normalizedSlug ||
      cat.name_en?.toLowerCase() === normalizedSlug ||
      cat.name_no?.toLowerCase() === normalizedSlug ||
      cat.name?.toLowerCase().replace(/\s+/g, '_') === slug.toLowerCase() ||
      cat.name_en?.toLowerCase().replace(/\s+/g, '_') === slug.toLowerCase() ||
      cat.name_no?.toLowerCase().replace(/\s+/g, '_') === slug.toLowerCase()
    );

    if (category) {
      this.selectedCategory = category.category_id;
      this.applyFilters();
    }
    this.pendingCategorySlug = null;
  }

  loadActiveDeals() {
    this.loadingDeals = true;
    this.endpointService.cor_coy_56().subscribe({
      next: (res) => {
        this.activeDeals = res.data || [];
        // Build map of company_id to deals
        this.companyDealsMap = {};
        for (const deal of this.activeDeals) {
          if (!this.companyDealsMap[deal.company_id]) {
            this.companyDealsMap[deal.company_id] = [];
          }
          this.companyDealsMap[deal.company_id].push(deal);
        }
        this.loadingDeals = false;
      },
      error: () => {
        this.activeDeals = [];
        this.companyDealsMap = {};
        this.loadingDeals = false;
      }
    });
  }

  hasActiveDeal(company: CompanyResponse): boolean {
    return !!this.companyDealsMap[company.company_id]?.length;
  }

  loadCategories() {
    this.loadingCategories = true;
    this.endpointService.cor_coy_32().subscribe({
      next: (res) => {
        this.categories = res.data || [];
        this.buildCategoryOptions();
        this.loadingCategories = false;
        // Apply pending category from query params
        if (this.pendingCategorySlug) {
          this.applyCategoryFromSlug(this.pendingCategorySlug);
        }
      },
      error: () => {
        this.categories = [];
        this.buildCategoryOptions();
        this.loadingCategories = false;
      }
    });
  }

  private buildCategoryOptions() {
    this.categoryOptions = this.categories.map(cat => ({
      id: cat.category_id,
      name: this.translateCategory(cat)
    }));
  }

  loadCompanies() {
    this.loading = true;
    const params: COR_COY_4 = {
      compact: true,  // Use compact mode for ~60% smaller payload
      company_type: 'stores_services'  // Server-side filter for better performance
    };

    // Use public endpoint (no auth required) with compact mode
    this.companyService.getPublicCompanies(params).subscribe({
      next: (response) => {
        this.companies = response.data || [];
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  applyFilters() {
    this.filteredCompanies = this.companies.filter(company => {
      // Category filter
      if (this.selectedCategory && company.category_id !== this.selectedCategory) {
        return false;
      }

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        const matchesSearch =
          company.company_name?.toLowerCase().includes(query) ||
          company.description?.toLowerCase().includes(query) ||
          company.city?.toLowerCase().includes(query) ||
          company.country?.toLowerCase().includes(query) ||
          company.category?.name?.toLowerCase().includes(query) ||
          company.category?.name_en?.toLowerCase().includes(query) ||
          company.category?.name_no?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      return true;
    });

    // Geocode companies for map
    this.geocodeCompanies();
  }

  onCategoryChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  getCategoryLabel(categoryId: number | null): string {
    if (!categoryId) return this.translateService.instant('storesServices.allCategories');
    const category = this.categories.find(c => c.category_id === categoryId);
    if (!category) return '';
    return this.translateCategory(category);
  }

  translateCategory(category: CompanyCategoryResponse | null | undefined): string {
    if (!category) return '';
    const lang = this.translateService.currentLang;
    if (lang === 'no') {
      return category.name_no || category.name_en || category.name || '';
    }
    return category.name_en || category.name || '';
  }

  getCategoryIcon(categoryId: number | null): string {
    if (!categoryId) return 'fa-th-large';
    const category = this.categories.find(c => c.category_id === categoryId);
    return category?.icon || 'fa-store';
  }

  getMarkerColor(company: CompanyResponse): 'red' | 'blue' | 'yellow' | 'purple' | 'orange' | 'pink' {
    // Assign colors based on category position in the categories array
    const categoryId = company.category_id;
    if (!categoryId) return 'blue';

    // Find the index of this category in the categories array
    const categoryIndex = this.categories.findIndex(c => c.category_id === categoryId);
    if (categoryIndex === -1) return 'blue';

    const colors: ('blue' | 'purple' | 'orange' | 'red' | 'yellow' | 'pink')[] = ['blue', 'purple', 'orange', 'red', 'yellow', 'pink'];
    return colors[categoryIndex % colors.length];
  }

  async geocodeCompanies() {
    this.geocodingInProgress = true;
    this.mapMarkers = [];

    const companiesToGeocode = this.filteredCompanies.filter(
      company => company.address || company.city || company.country
    );

    for (const company of companiesToGeocode) {
      const result = await this.geocodingService.geocodeAddress(
        company.address || '',
        company.city || '',
        company.postal_code || '',
        company.country || ''
      );

      if (result.success && result.lat && result.lng) {
        // Cache coordinates for distance calculation
        this.companyCoordinates[company.company_id] = { lat: result.lat, lng: result.lng };

        // Calculate distance for popup display
        let distanceStr: string | undefined;
        if (this.userLocation) {
          const distKm = this.calculateDistance(
            this.userLocation.lat,
            this.userLocation.lng,
            result.lat,
            result.lng
          );
          distanceStr = this.formatDistance(distKm);
        }

        this.mapMarkers = [...this.mapMarkers, {
          id: company.company_id,
          lat: result.lat,
          lng: result.lng,
          title: company.company_name || 'Unknown',
          subtitle: this.formatCompanySubtitle(company),
          imageUrl: company.company_logo_url || company.header_image_url || undefined,
          color: this.getMarkerColor(company),
          distance: distanceStr,
          data: company
        }];
      }
    }

    this.geocodingInProgress = false;

    // Re-sort if sorting by distance is enabled
    if (this.sortByDistance && this.userLocation) {
      this.sortCompaniesByDistance();
    }
  }

  private sortCompaniesByDistance() {
    if (!this.userLocation) return;

    this.filteredCompanies.sort((a, b) => {
      const distA = this.getCompanyDistance(a);
      const distB = this.getCompanyDistance(b);
      if (distA === null && distB === null) return 0;
      if (distA === null) return 1;
      if (distB === null) return -1;
      return distA - distB;
    });
  }

  private formatCompanySubtitle(company: CompanyResponse): string {
    const parts = [];
    const categoryName = this.translateCategory(company.category);
    if (categoryName) parts.push(categoryName);
    if (company.city) parts.push(company.city);
    if (company.country) parts.push(company.country);
    return parts.join(', ');
  }

  onMapMarkerClick(marker: MapMarker) {
    // No scroll behavior - user can click "View Details" in the info window
  }

  onMapInfoWindowAction(marker: MapMarker) {
    this.router.navigate(['/stores-services', marker.id]);
  }

  onSearchChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  clearFilters() {
    this.searchQuery = '';
    this.selectedCategory = null;
    this.sortByDistance = false;
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  toggleSortByDistance() {
    this.sortByDistance = !this.sortByDistance;
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  getUserLocation() {
    if (!navigator.geolocation) {
      return;
    }

    this.locationLoading = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.locationLoading = false;
      },
      () => {
        this.locationLoading = false;
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  getCompanyDistance(company: CompanyResponse): number | null {
    if (!this.userLocation) return null;
    const coords = this.companyCoordinates[company.company_id];
    if (!coords) return null;
    return this.calculateDistance(
      this.userLocation.lat,
      this.userLocation.lng,
      coords.lat,
      coords.lng
    );
  }

  formatDistance(km: number | null): string {
    if (km === null) return '';
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${Math.round(km)} km`;
  }

  getCompanyInitials(company: CompanyResponse): string {
    if (!company.company_name) return '?';
    const words = company.company_name.split(' ');
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return company.company_name.substring(0, 2).toUpperCase();
  }
}
